/**
 * Admission controller that paces sandboxed-iframe creation instead of letting
 * a canvas full of HTML blocks mount every frame in one burst.
 *
 * Why this exists: on the public share view every block node used to mount its
 * srcdoc iframe simultaneously (React Flow renders all nodes regardless of
 * viewport). On block-heavy canvases that first-paint burst — dozens of
 * documents each parsing fonts, injected CSS, the block runtime, and infinite
 * CSS animations inside ~1s — reliably killed the WebKit renderer process on
 * iPhones (mach-port EXC_GUARD, "Webpage Crashed"). Pacing the mounts keeps
 * the same end state while staying under what WebKit tolerates.
 *
 * Behavior:
 * - Cards register their host element once their document is READY to mount
 *   (srcDoc prepared — see useIframeMountAdmission), so an admission is
 *   always immediately followed by a real iframe mount and the load event is
 *   what returns the slot. Registering un-ready cards would let slots leak
 *   through the fallback timeout while fetches are still in flight, and a
 *   slow network would then re-cluster the very burst this exists to prevent.
 * - One shared IntersectionObserver ranks waiting cards by viewport proximity
 *   (IO measures the on-screen rect, so it works under React Flow's CSS
 *   transforms).
 * - Admissions start only after a settle delay from the first registration,
 *   so the initial rasterization happens at the post-fitView scale rather
 *   than mid-animation.
 * - At most MAX_CONCURRENT_INITS iframes are initializing at once; a slot
 *   frees on the iframe's load event, with a generous fallback timeout as a
 *   stuck-detector (a document whose load never fires must not wedge the
 *   queue). A minimum spacing between admissions spreads compositor work;
 *   fully offscreen cards trickle at a slower cadence.
 * - Admission is ONE-WAY: nothing is ever un-admitted on scroll-away. (The
 *   rejected alternative — React Flow's `onlyRenderVisibleElements` — churns
 *   iframes on every pan, which recreates exactly the load spikes that crash
 *   WebKit.)
 */

/** Iframes allowed to be initializing (admitted, not yet loaded) at once. */
const MAX_CONCURRENT_INITS = 2;
/** Minimum gap between admissions for near-viewport cards. */
const ADMIT_SPACING_MS = 150;
/** Slower cadence once only fully-offscreen cards remain. */
const OFFSCREEN_TRICKLE_SPACING_MS = 250;
/**
 * Stuck-detector ONLY: a slot frees early if an admitted iframe never reports
 * load (dead frame, broken subresource). Sized well above a slow device's
 * worst-case document init so the load event — not this timer — is what
 * returns slots in normal operation; a short value here would silently turn
 * the concurrency cap into a mere admission-rate limit.
 */
const INIT_FALLBACK_TIMEOUT_MS = 5000;
/** Wait out node measurement + the fitView animation before admitting. */
const START_DELAY_MS = 600;
/**
 * Pre-admit cards within half a viewport of the visible rect. Kept modest on
 * purpose: intersectionRatio is measured against the margin-EXPANDED root, so
 * a large margin would report ratio 1 for everything anywhere near the
 * viewport and collapse the viewport-first ranking into registration order.
 */
const OBSERVER_ROOT_MARGIN = "50%";

export interface IframeMountHandle {
  /** The admitted iframe finished loading — frees its concurrency slot. */
  loaded(): void;
  /** Unregister (host unmounted). Safe to call at any time, and idempotent. */
  release(): void;
}

interface Entry {
  element: Element;
  onAdmit: () => void;
  order: number;
  /** Last intersection ratio; 0 until the observer reports (or without IO). */
  ratio: number;
  admitted: boolean;
  /** Load reported, fallback timeout fired, or released — slot returned. */
  settled: boolean;
  fallbackTimer: ReturnType<typeof setTimeout> | null;
}

type ObserverFactory = (
  onEntries: (entries: IntersectionObserverEntry[]) => void,
) => IntersectionObserver | null;

const defaultObserverFactory: ObserverFactory = (onEntries) => {
  // Environments without IntersectionObserver (jsdom, ancient browsers)
  // degrade to registration-order trickle — pacing still applies.
  if (typeof IntersectionObserver === "undefined") return null;
  return new IntersectionObserver(onEntries, {
    rootMargin: OBSERVER_ROOT_MARGIN,
    threshold: [0, 0.01, 0.25, 0.5, 0.75, 1],
  });
};

export class IframeMountScheduler {
  private entries = new Map<Element, Entry>();
  /** `undefined` = not built yet (lazy); `null` = IO unavailable. */
  private observer: IntersectionObserver | null | undefined;
  private nextOrder = 0;
  private started = false;
  private startTimer: ReturnType<typeof setTimeout> | null = null;
  private pumpTimer: ReturnType<typeof setTimeout> | null = null;
  private pumpDueAt = Infinity;
  private initializing = 0;
  private lastAdmitAt: number | null = null;

  constructor(
    private readonly createObserver: ObserverFactory = defaultObserverFactory,
  ) {}

  register(element: Element, onAdmit: () => void): IframeMountHandle {
    if (this.observer === undefined) {
      this.observer = this.createObserver(this.handleObserverEntries);
    }

    // A lingering registration for the same element (remount race, Fast
    // Refresh) would let its stale handle evict THIS entry later — release()
    // must therefore also check identity, and the old entry goes now so the
    // map and observer only ever know one entry per element.
    const prior = this.entries.get(element);
    if (prior) this.release(prior);

    const entry: Entry = {
      element,
      onAdmit,
      order: this.nextOrder++,
      ratio: 0,
      admitted: false,
      settled: false,
      fallbackTimer: null,
    };
    this.entries.set(element, entry);
    this.observer?.observe(element);

    if (!this.started && this.startTimer === null) {
      this.startTimer = setTimeout(() => {
        this.startTimer = null;
        this.started = true;
        this.pump();
      }, START_DELAY_MS);
    } else if (this.started) {
      this.schedulePump();
    }

    return {
      loaded: () => this.settle(entry),
      release: () => this.release(entry),
    };
  }

  private handleObserverEntries = (
    observed: IntersectionObserverEntry[],
  ): void => {
    for (const io of observed) {
      const entry = this.entries.get(io.target);
      if (!entry || entry.admitted) continue;
      entry.ratio = io.intersectionRatio;
    }
    // No pump here: admissions are slot/time driven, and the next pump reads
    // the freshest ratios when it fires.
  };

  /** Highest priority waiting entry: viewport-first, then registration order. */
  private pickNext(): Entry | null {
    let best: Entry | null = null;
    for (const entry of this.entries.values()) {
      if (entry.admitted) continue;
      if (
        !best ||
        entry.ratio > best.ratio ||
        (entry.ratio === best.ratio && entry.order < best.order)
      ) {
        best = entry;
      }
    }
    return best;
  }

  private pump = (): void => {
    if (!this.started || this.initializing >= MAX_CONCURRENT_INITS) return;

    const next = this.pickNext();
    if (!next) return;

    // ratio > 0 ⇔ within the observer's expanded root (or admitted blind
    // without IO — that path always trickles).
    const spacing =
      next.ratio > 0 ? ADMIT_SPACING_MS : OFFSCREEN_TRICKLE_SPACING_MS;
    const sinceLast =
      this.lastAdmitAt === null
        ? Infinity
        : performance.now() - this.lastAdmitAt;
    if (sinceLast < spacing) {
      this.schedulePump(spacing - sinceLast);
      return;
    }

    next.admitted = true;
    this.initializing++;
    this.lastAdmitAt = performance.now();
    this.observer?.unobserve(next.element);
    next.fallbackTimer = setTimeout(
      () => this.settle(next),
      INIT_FALLBACK_TIMEOUT_MS,
    );
    next.onAdmit();

    if (this.initializing < MAX_CONCURRENT_INITS) {
      this.schedulePump(spacing);
    }
  };

  private schedulePump(delayMs = 0): void {
    // performance.now(): monotonic. Wall-clock here would let a backward
    // clock step (NTP, sleep-resume) arm a multi-minute timer that the
    // earlier-deadline check below could then never displace.
    const dueAt = performance.now() + delayMs;
    if (this.pumpTimer !== null) {
      // Keep the EARLIER of the two wake-ups: a freed slot's zero-delay pump
      // must not be swallowed by an already-armed spacing timer.
      if (dueAt >= this.pumpDueAt) return;
      clearTimeout(this.pumpTimer);
    }
    this.pumpDueAt = dueAt;
    this.pumpTimer = setTimeout(() => {
      this.pumpTimer = null;
      this.pumpDueAt = Infinity;
      this.pump();
    }, delayMs);
  }

  /** Return an admitted entry's slot (load event, timeout, or release). */
  private settle(entry: Entry): void {
    if (!entry.admitted || entry.settled) return;
    entry.settled = true;
    if (entry.fallbackTimer !== null) {
      clearTimeout(entry.fallbackTimer);
      entry.fallbackTimer = null;
    }
    this.initializing--;
    this.schedulePump();
  }

  private release(entry: Entry): void {
    // Identity, not key presence: a stale handle (this element re-registered
    // since) must not evict the live entry or double-return its slot.
    if (this.entries.get(entry.element) !== entry) return;
    this.entries.delete(entry.element);
    this.observer?.unobserve(entry.element);
    if (entry.admitted && !entry.settled) {
      this.settle(entry);
    }
    if (this.entries.size === 0) this.resetWhenIdle();
  }

  /** Empty again (canvas unmounted): the next canvas gets a fresh settle
   *  window instead of inheriting a started scheduler mid-flight. */
  private resetWhenIdle(): void {
    this.started = false;
    if (this.startTimer !== null) {
      clearTimeout(this.startTimer);
      this.startTimer = null;
    }
    if (this.pumpTimer !== null) {
      clearTimeout(this.pumpTimer);
      this.pumpTimer = null;
    }
    this.pumpDueAt = Infinity;
    this.initializing = 0;
    this.lastAdmitAt = null;
  }
}

/** Shared instance — all cards on a page pace through one controller. */
export const iframeMountScheduler = new IframeMountScheduler();
